uniform float uTime;
uniform float uDeltaTime;
uniform sampler2D uBase;
uniform sampler2D uBase2;
uniform float uMorphAmount;
uniform vec3 uMouse;
uniform float uMouseRadius;
uniform float uMouseAspect;
uniform float uSuckAmount;
uniform float uMouseTrail;
uniform float uDecayRate;
uniform float uSuctionBarsEnabled;
uniform float uSuctionBarStrength;
uniform float uSuctionBarRadius;
uniform float uSuctionKillFactor;
uniform vec3 uSuctionBar0Pos;
uniform vec3 uSuctionBar0Size;
uniform vec3 uSuctionBar1Pos;
uniform vec3 uSuctionBar1Size;
uniform float uCameraZ;
uniform float uFlatten;
uniform float uTextTipScale;
uniform float uTextTipDown;
uniform float uTextSolid;
uniform float uSpawnNoiseFreq;
uniform float uSpawnNoiseSpeed;
uniform float uSpawnNoiseAmp;
uniform float uSpawnNoiseGate;
uniform float uSpawnBottomOnly; // 1 = si nasce solo nella fascia offscreen (sopra lo schermo, flusso discendente), 0 = ovunque
uniform float uSpawnBottomY; // quota della fascia di spawn (sopra lo schermo)
uniform float uSpawnBottomSpread; // semi-larghezza orizzontale della fascia di spawn
uniform float uSpawnEdgeBias; // esponente allo stato finale: <1 = piu' spawn ai bordi (1 = uniforme)
uniform float uSpawnEdgeAmount; // 0..1: quanto il bias e' attivo (0 = uniforme, 1 = pieno). Guidato dall'espansione dell'area repulsiva
uniform float uSpawnEdgeFloor; // 0..1: quota di spawn uniforme miscelata, garantisce una densita' minima al centro (0 = centro vuoto)
uniform float uPathKillY; // sopra questa quota la particella viene riciclata (default: mai)
uniform float uPathKinematic; // 1 = percorso deterministico, niente fisica
uniform float uPathSpread;
uniform float uPathSpeed;
uniform float uPathWaveFreq;
uniform float uPathWaveAmp;
uniform float uPathWaveSpeed;
uniform vec4 uTextBox0; // xy = centro, zw = half-size (piano z=0) — sagoma del campo di forza
uniform float uTextBox0Strength;
uniform vec4 uTextBox1;
uniform float uTextBox1Strength;
uniform vec4 uTextCore0; // capsula dura sul rettangolo reale del testo
uniform vec4 uTextCore1;
uniform vec2 uTextTips0; // x = punta sopra, y = punta sotto (unita' mondo)
uniform vec2 uTextTips1;
uniform float uTextDodge; // 1 = schivata cinematica (rimappatura x), 0 = forze
uniform float uDomainHalfW;

#include ../includes/simplexNoise4d.glsl
#include ../includes/textShapeSdf.glsl

float distToBar(vec3 pos, vec3 center, vec3 halfSize)
{
    vec3 d = abs(pos - center) - halfSize;
    return length(max(d, 0.0));
}

// Garanzia dura: una particella non puo' MAI trovarsi sopra il testo.
// Capsula (rounded box) sul rettangolo reale del testo, scalata alla profondita'
// della particella; se dentro, proiettata sulla superficie lungo la normale.
vec3 pushOutOfTextCapsule(vec3 pos, vec4 box)
{
    // Prisma infinito lungo z: la capsula vale a ogni profondita'
    vec2 h = box.zw;
    vec2 p = pos.xy - box.xy;
    float r = min(h.x, h.y);
    vec2 q = abs(p) - h + r;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
    if(sd >= 0.0) return pos;
    vec2 sgn = vec2(p.x >= 0.0 ? 1.0 : -1.0, p.y >= 0.0 ? 1.0 : -1.0);
    vec2 n;
    if(q.x > 0.0 && q.y > 0.0) n = normalize(q) * sgn;
    else if(q.x > q.y) n = vec2(sgn.x, 0.0);
    else n = vec2(0.0, sgn.y);
    pos.xy -= n * sd;
    return pos;
}

// Riloca un punto di spawn fuori dalla sagoma del testo, SOLO in orizzontale:
// la quota non cambia mai, cosi' chi nasce offscreen resta offscreen
// (spostarlo lungo la normale poteva portarlo dentro lo schermo)
vec3 relocateOutsideTextShape(vec3 pos, vec4 box, vec2 tips, float strength, float rnd)
{
    if(strength < 0.01) return pos;
    float g = gapHalfWidth(pos.y - box.y, box.zw, tips.x, tips.y);
    if(g < 0.001) return pos;
    g = g * 1.05 + 0.2;
    float d = pos.x - box.x;
    if(abs(d) < g)
        pos.x = box.x + (d >= 0.0 ? 1.0 : -1.0) * (g + rnd * 2.0);
    return pos;
}

float rectDist(vec3 pos, vec3 center, float radius, float aspect)
{
    vec3 d = abs(pos - center);
    float dx = d.x / (radius * aspect);
    float dy = d.y / (radius / aspect);
    float dz = d.z / radius;
    return max(max(dx, dy), dz);
}

void main()
{
    float time = uTime * 0.2;
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec4 particle = texture(uParticles, uv);

    // --- Percorso cinematico (pagina carbon): NIENTE fisica. La posizione e' una
    // funzione deterministica del tempo: corsia verticale + serpentina armonica.
    // Le corsie vengono rimappate analiticamente ai lati del gap del testo
    // (strizzatura orizzontale riga per riga): il testo non puo' essere toccato
    // per costruzione, nessuna collisione, nessun accumulo.
    if(uPathKinematic > 0.5)
    {
        float h1 = fract(sin(dot(uv, vec2(12.9898, 78.233))) * 43758.5453);
        float h2 = fract(sin(dot(uv, vec2(39.3468, 11.135))) * 24634.6345);
        float h3 = fract(sin(dot(uv, vec2(73.156, 52.235))) * 9284.1234);

        float lane = (h1 * 2.0 - 1.0) * uPathSpread;
        float zP = (h2 * 2.0 - 1.0) * 1.5;
        float speed = uPathSpeed * (0.75 + 0.5 * h3);
        float bottom = uSpawnBottomY;
        float span = max(uPathKillY - bottom, 0.001);
        float y = bottom + mod(h2 * 37.77 * span + uTime * speed, span);

        float ff = 0.6 + h3 * 1.2;
        float ph = h2 * 6.2831853;
        float wave = uPathWaveAmp * sin(y * uPathWaveFreq * ff + ph + uTime * uPathWaveSpeed)
                   + uPathWaveAmp * 0.5 * sin(y * uPathWaveFreq * ff * 2.7 + ph * 1.7 - uTime * uPathWaveSpeed * 0.6);
        float x = lane + wave;

        // Gap alla quota y (entrambe le scritte) — prisma: uguale a ogni profondita'
        float gap = 0.0;
        float cx = 0.0;
        if(uTextBox0Strength > 0.01)
        {
            float g0 = gapHalfWidth(y - uTextBox0.y, uTextBox0.zw, uTextTips0.x, uTextTips0.y);
            if(g0 > gap) { gap = g0; cx = uTextBox0.x; }
        }
        if(uTextBox1Strength > 0.01)
        {
            float g1 = gapHalfWidth(y - uTextBox1.y, uTextBox1.zw, uTextTips1.x, uTextTips1.y);
            if(g1 > gap) { gap = g1; cx = uTextBox1.x; }
        }
        gap *= 1.06; // margine di sicurezza

        if(gap > 0.001)
        {
            float W = uPathSpread + uPathWaveAmp * 1.5 + 0.5;
            float side = (x - cx) >= 0.0 ? 1.0 : -1.0;
            float availScale = max(W - gap, 0.0) / W;
            x = cx + side * (gap + abs(x - cx) * availScale);
        }

        gl_FragColor = vec4(x, y, zP, 0.35);
        return;
    }

    // Dead — respawn
    if(particle.a >= 1.0)
    {
        particle.a = mod(particle.a, 1.0);

        // Some particles spawn from mouse as a smoke trail
        float spawnNoise = simplexNoise4d(vec4(uv * 100.0, time * 5.0, 0.0));
        if(uMouse.x < 9000.0 && uMouseTrail > 0.0 && uSuckAmount < 0.1 && spawnNoise > (1.0 - uMouseTrail))
        {
            vec3 offset = vec3(
                simplexNoise4d(vec4(uv * 50.0, time * 10.0, 1.0)),
                simplexNoise4d(vec4(uv * 50.0, time * 10.0, 2.0)),
                simplexNoise4d(vec4(uv * 50.0, time * 10.0, 3.0))
            ) * 0.3;
            particle.xyz = uMouse + offset;
        }
        else
        {
            vec3 base1 = texture(uBase, uv).xyz;
            vec3 base2 = texture(uBase2, uv).xyz;
            vec3 spawnPos = mix(base1, base2, uMorphAmount);

            // Pattern di rinascita organico (perlin-like): un campo di noise che
            // evolve nel tempo decide dove si puo' rinascere (nuvole in movimento)
            // e sposta il punto di spawn con un jitter coerente
            if(uSpawnNoiseAmp > 0.001 || uSpawnNoiseGate > -0.999)
            {
                float t = uTime * uSpawnNoiseSpeed;
                float field = simplexNoise4d(vec4(spawnPos * uSpawnNoiseFreq, t));
                if(field < uSpawnNoiseGate)
                {
                    particle.a = 1.0; // zona "spenta" del pattern: ritenta al prossimo frame
                }
                else
                {
                    vec3 jitter = vec3(
                        simplexNoise4d(vec4(spawnPos * uSpawnNoiseFreq * 2.0 + 13.1, t)),
                        simplexNoise4d(vec4(spawnPos * uSpawnNoiseFreq * 2.0 + 47.3, t)),
                        simplexNoise4d(vec4(spawnPos * uSpawnNoiseFreq * 2.0 + 89.7, t)) * 0.3
                    );
                    spawnPos += jitter * uSpawnNoiseAmp;
                }
            }
            particle.xyz = spawnPos;
        }

        // Reveal: all'inizio si nasce solo in una fascia sopra lo schermo (le
        // particelle scendono aspirate verso il basso, come i piani Falmec che
        // aspirano da sotto); quando l'aspirazione e' attiva la zona di spawn
        // si estende gradualmente a tutto lo schermo
        if(uSpawnBottomOnly > 0.001)
        {
            float pick = simplexNoise4d(vec4(uv * 60.0, time * 3.0, 5.0)) * 0.5 + 0.5;
            if(pick < uSpawnBottomOnly)
            {
                // Distribuzione uniforme su tutta la larghezza (hash, non noise:
                // il noise si concentra al centro e creerebbe pennacchi)
                float hx = fract(sin(dot(uv, vec2(127.1, 311.7)) + time * 7.0) * 43758.5453);
                float hy = fract(sin(dot(uv, vec2(269.5, 183.3)) + time * 5.0) * 43758.5453);
                // Bias verso i bordi: con esponente <1 la densita' di spawn cala
                // dal bordo verso il centro (piu' particelle ai lati, meno al centro
                // dove i testi repulsivi le ammasserebbero contro il contorno).
                // L'intensita' segue l'espansione dell'area repulsiva: a scritta
                // nascosta lo spawn resta uniforme (amount 0), a scritta piena e'
                // massimo (amount 1).
                float sxUniform = hx * 2.0 - 1.0;                  // [-1,1] uniforme
                float edgeExp = mix(1.0, uSpawnEdgeBias, uSpawnEdgeAmount);
                float sxBiased = sign(sxUniform) * pow(abs(sxUniform), edgeExp);
                // Floor: riporta una quota verso l'uniforme cosi' il centro conserva
                // sempre un minimo di particelle e non resta un buco netto
                float sx = mix(sxBiased, sxUniform, uSpawnEdgeFloor * uSpawnEdgeAmount);
                particle.x = sx * uSpawnBottomSpread;
                particle.y = uSpawnBottomY + hy * 3.0;
            }
        }

        // Mai dentro le sagome repulsive dei testi: riloca fuori dal contorno
        float rnd = simplexNoise4d(vec4(uv * 80.0, time * 4.0, 7.0)) * 0.5 + 0.5;
        particle.xyz = relocateOutsideTextShape(particle.xyz, uTextBox0, uTextTips0, uTextBox0Strength, rnd);
        particle.xyz = relocateOutsideTextShape(particle.xyz, uTextBox1, uTextTips1, uTextBox1Strength, rnd);

        // Nascita offscreen: salta la rampa di fade-in legata alla vita, altrimenti
        // con decay basso la particella resta quasi invisibile per meta' percorso
        if(uSpawnBottomOnly > 0.001) particle.a = 0.12;

        // Pre-kill (decay altissimo all'avvio): il passo di vita per frame e'
        // enorme e il reset costante sincronizzerebbe tutte le vite in poche
        // coorti che poi morirebbero in massa insieme (la simulazione sembra
        // interrompersi e ripartire). Jitter sulla vita di rinascita per
        // tenere le vite sparse quando il decay torna normale.
        if(uDecayRate > 5.0)
        {
            float hl = fract(sin(dot(uv, vec2(419.2, 371.9)) + time * 11.0) * 42817.345);
            particle.a = 0.12 + hl * 0.75;
        }

        // Modalita' 2D: si nasce sempre sul piano z=0
        if(uFlatten > 0.5) particle.z = 0.0;
    }
    // Alive
    else
    {
        vec4 velocity = texture(uVelocity, uv);
        particle.xyz += velocity.xyz * uDeltaTime;

        // Decay — particles near cursor despawn faster when sucking
        float decay = uDecayRate;
        if(uSuckAmount > 0.01) {
            float mouseDist = length(particle.xy - uMouse.xy);
            float fadeRadius = uMouseRadius * 0.6;
            float proximity = 1.0 - smoothstep(0.0, fadeRadius, mouseDist);
            // Use absolute minimum rate so it works even with low respawnRate
            float suckDecay = max(uDecayRate * 5.0, 3.0);
            decay = mix(decay, suckDecay, proximity * uSuckAmount);
        }
        // Bar suction also accelerates decay near bars
        if(uSuctionBarsEnabled > 0.5 && uSuctionBarStrength > 0.01)
        {
            float d0 = distToBar(particle.xyz, uSuctionBar0Pos, uSuctionBar0Size);
            float d1 = distToBar(particle.xyz, uSuctionBar1Pos, uSuctionBar1Size);
            float nearest = min(d0, d1);
            float fadeRadius = uSuctionBarRadius * uSuctionKillFactor;
            if(nearest < fadeRadius)
            {
                float proximity = 1.0 - smoothstep(0.0, fadeRadius, nearest);
                float barDecay = max(uDecayRate * 5.0, 3.0);
                decay = mix(decay, barDecay, proximity);
            }
        }

        // Dissolvenza graduale dalla quota di uscita fino al top: il decay
        // cresce dolcemente con la penetrazione — alcune particelle arrivano
        // fino in cima allo schermo sfumando lungo il percorso
        if(particle.y > uPathKillY)
        {
            float over = particle.y - uPathKillY;
            decay = max(decay, over * 1.0);
        }

        particle.a += uDeltaTime * decay;
    }

    // Schivata cinematica (pagina carbon): la x viene rimappata fuori dal gap.
    // E' una trasformazione continua e idempotente: garanzia esatta, zero muri
    if(uTextDodge > 0.5)
    {
        float gap = 0.0;
        float cx = 0.0;
        if(uTextBox0Strength > 0.01)
        {
            float g0 = gapHalfWidth(particle.y - uTextBox0.y, uTextBox0.zw, uTextTips0.x, uTextTips0.y);
            if(g0 > gap) { gap = g0; cx = uTextBox0.x; }
        }
        if(uTextBox1Strength > 0.01)
        {
            float g1 = gapHalfWidth(particle.y - uTextBox1.y, uTextBox1.zw, uTextTips1.x, uTextTips1.y);
            if(g1 > gap) { gap = g1; cx = uTextBox1.x; }
        }
        gap *= 1.05;
        if(gap > 0.001)
        {
            // Clamp idempotente al bordo del gap: chi e' fuori non viene toccato,
            // chi entrerebbe viene posato sul bordo. Il gap si allarga/stringe con
            // continuita' lungo la risalita, quindi la particella lo costeggia
            float d = particle.x - cx;
            if(abs(d) < gap) particle.x = cx + (d >= 0.0 ? gap : -gap);
        }
    }

    // Barriera fisica sul testo (capsula dura): solo in modalita' solida.
    // Con textAvoidSolid:false restano solo le forze repulsive.
    if(uTextSolid > 0.5)
    {
        if(uTextBox0Strength > 0.01) particle.xyz = pushOutOfTextCapsule(particle.xyz, uTextCore0);
        if(uTextBox1Strength > 0.01) particle.xyz = pushOutOfTextCapsule(particle.xyz, uTextCore1);
    }

    gl_FragColor = particle;
}
